import { useMemo, useState } from 'react';
import { MessageDto, MessageStatus, MessageStatusOptions } from 'common/dto-types/message-dto';
import BaseTable from 'components/base-table';
import { BaseTableFetchFuncProps } from 'components/base-table/dto';
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import { mapFiltersToSpecification, mapSearchParamToSpecification } from 'utils/filtering/map-filter-to-specification';
import { fetchMessageList } from '../api';
import MessageTableColumnConfig, {
    DefaultMessageTableColumnIdToSortBy,
    DefaultMessageTableSortingType,
    DefaultVisibleMessageColumns,
    SearchableMessageTableColumnConfig,
} from './message-list-column-configs';
import { Box, Button, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TournamentDto, { mapFdcpSeasonToTournamentDto } from 'common/dto-types/tournament-dto';
import AsyncSelectSearchField from 'components/async-select-search-field';
import FDCPServices from 'fdcp/fdcp-services';

type MessageListPageProps = {
    statuses: MessageStatus[];
    allowStatusFilter?: boolean;
};

const MessageListPage = ({ statuses, allowStatusFilter = true }: MessageListPageProps) => {
    const [messageList, setMessageList] = useState<Array<MessageDto>>([]);
    const [pageCount, setPageCount] = useState(0);
    const [tournamentFilter, setTournamentFilter] = useState<TournamentDto | null>(null);
    const defaultVisibleColumnConfig = useMemo(() => DefaultVisibleMessageColumns, []);
    const navigate = useNavigate();

    // Filter status options to only show statuses available for this page
    const availableStatusOptions = useMemo(() => {
        return MessageStatusOptions.filter((option) => statuses.includes(option.value as MessageStatus));
    }, [statuses]);

    // Customize column config based on allowStatusFilter and available statuses
    const defaultColumnConfig = useMemo(() => {
        return MessageTableColumnConfig.map((col) => {
            // Disable or customize status column filtering
            if (col.accessor === 'status') {
                if (!allowStatusFilter) {
                    return {
                        ...col,
                        disableFilters: true,
                        disableSortBy: true,
                    };
                }
                // Replace filter options with only available statuses
                return {
                    ...col,
                    filterOptions: availableStatusOptions,
                };
            }
            return col;
        });
    }, [allowStatusFilter, availableStatusOptions]);

    const fetchData: BaseTableFetchFuncProps = async ({
        pageSize,
        pageIndex,
        filters,
        searchTerm,
        sortingType,
        sortBy,
    }) => {
        // Add tournament filter if present
        if (tournamentFilter && !filters.some((f) => f.id === 'tournament')) {
            filters.push({ id: 'tournament', value: tournamentFilter.fdcpId });
        }

        // Only enforce status filter if user hasn't explicitly set one
        const statusFilter = filters.find((f) => f.id === 'status');

        if (!statusFilter) {
            // No status filter exists, add the default based on statuses prop
            filters.push({ id: 'status', value: statuses.join(';') });
        } else if (allowStatusFilter) {
            // Validate that user-selected status is within allowed statuses
            const selectedStatuses = statusFilter.value.split(';');
            const validStatuses = selectedStatuses.filter((s: string) => statuses.includes(s as MessageStatus));

            if (validStatuses.length === 0) {
                // If no valid statuses selected, use all available statuses
                statusFilter.value = statuses.join(';');
            } else {
                // Use only valid statuses
                statusFilter.value = validStatuses.join(';');
            }
        }

        const data = await fetchMessageList({
            pageIndex,
            pageSize,
            sortingType,
            sortBy,
            andSpecifications: mapFiltersToSpecification(filters),
            orSpecifications:
                searchTerm && searchTerm.length > 0
                    ? mapSearchParamToSpecification(searchTerm, SearchableMessageTableColumnConfig)
                    : undefined,
        });

        if (data) {
            setMessageList(data.elements);
            setPageCount(Math.ceil(data.total / pageSize));
        }
    };

    return (
        <MainCard content={false}>
            <Stack direction="column">
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button variant="contained" color="primary" onClick={() => navigate('/message/detail/new')}>
                            Add Message
                        </Button>
                    </Box>
                    <Box sx={{ minWidth: 400, maxWidth: 500 }}>
                        <AsyncSelectSearchField
                            fetchData={(inputValue) =>
                                FDCPServices.searchSeasonByName({ searchString: inputValue }).then((seasons) =>
                                    seasons.map((season) => mapFdcpSeasonToTournamentDto(season)),
                                )
                            }
                            getOptionLabel={(option) => `${option.name} - [${option.fdcpId}]`}
                            isOptionEqualToValue={(option, selectedValue) => {
                                return option.fdcpId === selectedValue.fdcpId;
                            }}
                            value={tournamentFilter}
                            setValue={setTournamentFilter}
                        />
                    </Box>
                </Box>
                <ScrollX>
                    <BaseTable
                        data={messageList}
                        defaultColumnIdToSortBy={DefaultMessageTableColumnIdToSortBy}
                        defaultSortingType={DefaultMessageTableSortingType}
                        fetchData={fetchData}
                        pageCount={pageCount}
                        defaultColumnConfig={defaultColumnConfig}
                        defaultVisibleColumnConfig={defaultVisibleColumnConfig}
                        key={tournamentFilter?.fdcpId ?? 0}
                    />
                </ScrollX>
            </Stack>
        </MainCard>
    );
};

export default MessageListPage;
